/**
 * Drizzle query builders + query keys for TUTORING (M3).
 *
 * Local-first consumption: `submission_version` travels via my_cohorts_student (mine) and
 * my_cohorts_teacher / org_admin_scope (my cohorts' = grading queue); `tutoring_settings` +
 * `assignment_review_policy` via academy_org (UI gating).
 */

import {
  and,
  asc,
  assignment_review_policy,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  submission_version,
  tutoring_settings,
} from "@space/powersync";

export const tutoringQueryKeys = {
  all: ["academy-tutoring"] as const,
  versions: (
    assignmentId: string | undefined,
    cohortId: string | undefined,
    userId: string | undefined,
  ) => ["academy-tutoring", "versions", assignmentId, cohortId, userId] as const,
  queue: (cohortId: string | undefined) => ["academy-tutoring", "queue", cohortId] as const,
  queueAll: ["academy-tutoring", "queue-all"] as const,
  settings: (orgId: string | undefined) => ["academy-tutoring", "settings", orgId] as const,
  reviewPolicy: (assignmentId: string | undefined) =>
    ["academy-tutoring", "review-policy", assignmentId] as const,
  assignmentsIndex: ["academy-tutoring", "assignments-index"] as const,
  usersIndex: ["academy-tutoring", "users-index"] as const,
};

/** A submission's version history (asc by version). */
export function getSubmissionVersionsQuery(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
) {
  return db
    .select()
    .from(submission_version)
    .where(
      and(
        eq(submission_version.assignment_id, assignmentId),
        eq(submission_version.cohort_id, cohortId),
        eq(submission_version.user_id, userId),
      ),
    )
    .orderBy(asc(submission_version.version));
}

/**
 * Grading queue: PENDING versions (outcome IS NULL) from the cohorts the
 * actor sees (the streams already scope to their cohorts). Ordered by age
 * for the UI's SLA calculation. No `cohortId` = all visible cohorts.
 */
export function getReviewQueueQuery(db: PowerSyncDrizzleDB, cohortId?: string) {
  const pending = isNull(submission_version.outcome);
  return db
    .select()
    .from(submission_version)
    .where(cohortId ? and(pending, eq(submission_version.cohort_id, cohortId)) : pending)
    .orderBy(asc(submission_version.submitted_at));
}

/** MOST RECENT pending version of a submission (to review). */
export function getLatestPendingVersionQuery(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
) {
  return db
    .select()
    .from(submission_version)
    .where(
      and(
        eq(submission_version.assignment_id, assignmentId),
        eq(submission_version.cohort_id, cohortId),
        eq(submission_version.user_id, userId),
      ),
    )
    .orderBy(desc(submission_version.version))
    .limit(1);
}

/** Per-org tutoring config (one row; may not exist → use fallback). */
export function getTutoringSettingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(tutoring_settings).where(eq(tutoring_settings.organization_id, orgId));
}

/** Per-assignment policy override (may not exist → inherits from the org). */
export function getReviewPolicyQuery(db: PowerSyncDrizzleDB, assignmentId: string) {
  return db
    .select()
    .from(assignment_review_policy)
    .where(eq(assignment_review_policy.assignment_id, assignmentId));
}
